import { HoverCard, useToken } from '@chakra-ui/react';
import { Tooltip, TooltipProps } from '../ui/tooltip';
import { ENVIRONMENT } from '@monetize/ui/config';
import React, { FC } from 'react';

type TooltipPlacement = NonNullable<TooltipProps['positioning']>['placement'];

/*
 * v2 prop → v3 translation, none of which is exposed to callers (same
 * "wrapper absorbs restructuring, keep v2's flat external API" precedent as
 * MPopoverWrapper/MCenterModal/MDropdownActions):
 * - `isDisabled`/`isOpen`/`hasArrow` → real Ark Tooltip prop names are
 *   `disabled`/`open`/`showArrow` (confirmed via `@zag-js/tooltip`'s
 *   `TooltipProps` and `frontend/src/components/ui/tooltip.tsx`).
 * - `placement` is unchanged in name/values but now lives under
 *   `positioning.placement`, same as Popover/Drawer.
 * - `shouldWrapChildren` has no Ark equivalent; `../ui/tooltip` implements
 *   v2's wrapping (a focusable `span` around non-element children such as a
 *   plain text label, or around every child under `shouldWrapChildren`),
 *   since `Tooltip.Trigger`'s `asChild` renders nothing for a text child.
 * - `openDelay`/`closeOnClick` are real Ark prop names already, unchanged.
 * - An empty `label` renders the bare children, as v2 did. Ark still opens
 *   on an empty label, which paints a tiny dark pill under the trigger on
 *   hover.
 * - `bg` (default `tPurple.dark`, as v2's wrapper passed to the tooltip)
 *   sets `--tooltip-bg`, which the recipe paints both the bubble and the
 *   arrow from. Without it, tooltips showed the theme's #1A1B2E instead.
 */
export interface MTooltipProps extends Omit<
  TooltipProps,
  'content' | 'disabled' | 'open' | 'showArrow' | 'positioning'
> {
  label: React.ReactNode;
  placement?: TooltipPlacement;
  usePopover?: boolean;
  isDisabled?: boolean;
  isOpen?: boolean;
  hasArrow?: boolean;
  bg?: string;
}

export const MTooltip: FC<MTooltipProps> = React.forwardRef<
  HTMLDivElement,
  MTooltipProps
>(
  (
    {
      label,
      children,
      placement,
      size,
      bg = 'tPurple.dark',
      usePopover = false,
      isDisabled,
      isOpen,
      hasArrow,
      contentProps,
      ...rest
    },
    ref,
  ) => {
    const bgColor = ENVIRONMENT.NODE_ENV === 'test' ? {} : { bg };
    const [tooltipBg] = useToken('colors', bg);

    if (!usePopover) {
      if (!label) {
        return children;
      }

      return (
        <Tooltip
          content={label}
          size={size}
          ref={ref}
          disabled={isDisabled}
          open={isOpen}
          showArrow={hasArrow}
          positioning={{
            placement,
          }}
          contentProps={{
            ...contentProps,
            css: { '--tooltip-bg': tooltipBg, ...contentProps?.css },
          }}
          {...rest}
        >
          {children}
        </Tooltip>
      );
    } else {
      return (
        <HoverCard.Root
          lazyMount
          unmountOnExit
          size={size}
          positioning={{
            placement: placement || 'bottom-end',
            offset: { mainAxis: 0, crossAxis: 0 },
          }}
        >
          <HoverCard.Trigger asChild>{children}</HoverCard.Trigger>
          <HoverCard.Positioner>
            <HoverCard.Content bgColor={bgColor.bg} color="tWhite.base" p="2">
              {label}
            </HoverCard.Content>
          </HoverCard.Positioner>
        </HoverCard.Root>
      );
    }
  },
);
